import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { UserListRow, UserRole } from '@smile/shared';
import { USER_ROLES } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { UserForm, errText } from '../components/UserForm';
import { CZK, ROLE_LABEL } from '../lib/format';
import { longDt, pageList, pageParam, SEARCH_DEBOUNCE_MS } from '../lib/filters';
import { can } from '../lib/perms';

/**
 * Uživatelé — the legacy `users` list: ID · Jméno · Email · Status · Ceník ·
 * Provize · Promo · Rezervací · Poslední přihlášení, a status filter and a
 * search box, "Přidat Partnera/Uživatele". Reading is the manager's
 * /users/* grant; the form and the password reset are the administrator's.
 */
const STATUS_OPTIONS: Array<[string, string]> = [
  ['', 'Všichni'],
  ['ok', 'Aktivní'],
  ...USER_ROLES.map((r) => [r, ROLE_LABEL[r]] as [string, string]),
];

function forbidden() {
  return (
    <div className="container">
      <div className="pagehead">
        <h1>Uživatelé</h1>
      </div>
      <div className="card state">Seznam účtů je přístupný manažerům a administrátorům. Svůj účet najdete pod svým jménem vpravo nahoře — Můj účet.</div>
    </div>
  );
}

export function Uzivatele() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const status = STATUS_OPTIONS.some(([k]) => k === (sp.get('status') ?? '')) ? (sp.get('status') ?? '') : '';
  const page = pageParam(sp.get('p'), 20_000);
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/users/*', 'write');

  const [term, setTerm] = useState(q);
  const settled = useRef(q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (term.trim() === settled.current) return;
      settled.current = term.trim();
      apply({ q: term.trim(), p: '1' });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const apply = (patch: Record<string, string>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v && !(k === 'p' && v === '1')) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: true });
  };

  const params = useMemo(() => ({ q: q || undefined, status: status || undefined, page }), [q, status, page]);
  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey: ['users', params],
    queryFn: () => api.users(params),
    placeholderData: keepPreviousData,
  });
  if (isError && error instanceof ApiError && error.status === 403) return forbidden();
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Uživatelé</h1>
        <span className="sub recordcount">
          {data ? `${CZK.format(data.total)} ${data.total === 1 ? 'účet' : data.total < 5 ? 'účty' : 'účtů'}` : '…'}
          {isFetching && !isPending ? ' · aktualizuji' : ''}
        </span>
        <div className="acts">
          {canWrite && (
            <Link to="/uzivatele/novy" className="btn primary">
              + Přidat partnera / uživatele
            </Link>
          )}
          <form onSubmit={(e) => e.preventDefault()} role="search">
            <input type="search" placeholder="jméno, e-mail, firma, promo…" value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Hledat účty" />
          </form>
        </div>
      </div>

      <div className="card toolbar">
        <select value={status} onChange={(e) => apply({ status: e.target.value, p: '1' })} aria-label="Status">
          {STATUS_OPTIONS.map(([k, label]) => (
            <option key={k || 'all'} value={k}>
              {label}
            </option>
          ))}
        </select>
        <Link to="/nastaveni" className="btn sm" style={{ marginLeft: 'auto' }}>
          ← Nastavení
        </Link>
      </div>

      <div className="card tablecard" style={{ marginTop: 14 }}>
        {isPending ? (
          <div className="state" role="status" aria-busy="true">
            Načítám…
          </div>
        ) : isError ? (
          <div className="state" role="alert">
            Účty se nepodařilo načíst.
          </div>
        ) : (
          <div className="tablewrap sestavawrap">
            <table className="users">
              <thead>
                <tr>
                  <th className="num">ID</th>
                  <th>Jméno</th>
                  <th>E-mail</th>
                  <th>Status</th>
                  <th>Ceník</th>
                  <th className="num">Provize</th>
                  <th>Promo</th>
                  <th className="num">Rezervací</th>
                  <th>Poslední přihlášení</th>
                </tr>
              </thead>
              <tbody>
                {data!.rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="state">
                      Žádný účet neodpovídá.
                    </td>
                  </tr>
                )}
                {data!.rows.map((u: UserListRow) => (
                  <tr key={u.id} className={u.role === 'deleted' ? 'deleted' : u.role === 'disabled' ? 'blocked' : undefined}>
                    <td className="num was">{u.id}</td>
                    <td>
                      <b>
                        <Link to={`/uzivatele/${u.id}`}>{u.name || <span className="was">(bez jména)</span>}</Link>
                      </b>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`stav ${u.role === 'deleted' || u.role === 'disabled' ? 'warn' : u.role === 'admin' ? '' : 'gray'}`}>{ROLE_LABEL[u.role as UserRole] ?? u.role}</span>
                    </td>
                    <td>
                      {u.pricelistTitle || <span className="was">#{u.pricelistId}</span>}
                      {u.invoicePayment && <span className="stav gray" style={{ marginLeft: 6 }}>Fakturace CK</span>}
                    </td>
                    <td className="num">{u.provision ? `${u.provision} %` : ''}</td>
                    <td>
                      {u.promos.map((p) => (
                        <code key={p} className="promo">
                          {p}
                        </code>
                      ))}
                    </td>
                    <td className="num">{u.reservationCount ? CZK.format(u.reservationCount) : ''}</td>
                    <td className="was">{u.lastLogin ? longDt(u.lastLogin) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <div className="pager">
            <span>
              Strana {data.page} z {CZK.format(totalPages)} · {CZK.format(data.total)} účtů
            </span>
            <span className="pages">
              {pageList(data.page, totalPages).map((p, i) =>
                p === '…' ? (
                  <span key={`e${i}`} className="gap">
                    …
                  </span>
                ) : (
                  <button key={p} type="button" className={p === data.page ? 'pg on' : 'pg'} onClick={() => apply({ p: String(p) })}>
                    {p}
                  </button>
                ),
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** One account: the form, and the administrator's password reset. */
export function UzivatelDetail() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const isNew = idParam === 'novy';
  const id = isNew ? 0 : Number(idParam);
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/users/*', 'write');
  const q = useQuery({ queryKey: ['users', 'one', id], queryFn: () => api.user(id), enabled: !isNew && Number.isInteger(id) && id > 0 });
  const [shown, setShown] = useState<{ id: number; password: string; created?: boolean } | null>(null);
  const reset = useMutation({
    mutationFn: () => api.resetPassword(id),
    onSuccess: (out) => setShown(out),
  });

  if (q.isError && q.error instanceof ApiError && q.error.status === 403) return forbidden();

  return (
    <div className="container">
      <div className="pagehead">
        <h1>{isNew ? 'Nový účet' : q.data ? q.data.name || q.data.email : 'Účet'}</h1>
        {q.data && <span className="sub">#{q.data.id} · {q.data.email} · {ROLE_LABEL[q.data.role]}</span>}
        <div className="acts">
          <Link to="/uzivatele" className="btn sm">
            ← Uživatelé
          </Link>
        </div>
      </div>

      {shown && (
        <div className="notice" role="status">
          {shown.created ? 'Účet založen. ' : 'Heslo nastaveno. '}
          Nové heslo účtu #{shown.id}: <b className="pw">{shown.password}</b> — zobrazuje se jen teď, předejte ho osobně; uloženo je jen
          zašifrované.
        </div>
      )}

      {!canWrite && !isNew && q.data && (
        <div className="notice" role="note">
          Účty upravuje administrátor; tady je jen ke čtení.
        </div>
      )}

      {isNew ? (
        canWrite ? (
          <UserForm
            user={null}
            mode="admin"
            onCreated={(newId, password) => {
              setShown({ id: newId, password, created: true });
              navigate(`/uzivatele/${newId}`, { replace: true });
            }}
          />
        ) : (
          forbidden()
        )
      ) : q.isPending ? (
        <div className="card state">Načítám…</div>
      ) : q.isError ? (
        <div className="card state" role="alert">
          Účet se nepodařilo načíst.
        </div>
      ) : (
        <>
          <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
            <UserForm user={q.data} mode="admin" />
          </fieldset>
          {canWrite && q.data.id !== me.data?.id && (
            <div className="card detailcard" style={{ marginTop: 14 }}>
              <h2>Heslo</h2>
              <p className="settings-note">
                {q.data.legacyPassword ? 'Heslo je uložené starým způsobem (bez soli); ' : ''}
                Nové heslo se vygeneruje a zobrazí jednou, tady. Staré přestane platit hned — v obou systémech.
              </p>
              <div className="efactions">
                <button type="button" className="btn" disabled={reset.isPending} onClick={() => reset.mutate()}>
                  {reset.isPending ? 'Generuji…' : 'Vygenerovat nové heslo'}
                </button>
                {errText(reset.error) && (
                  <span className="arrive-error" role="alert">
                    {errText(reset.error)}
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
